import { AiIconSmall } from '@product-framework/components/ArtificalIntelligence/AiIcon';
import permissionConfig from './permissionConfig';

const menu = ({
  navigate,
  userAccess,
  store,
  IconLibrary,
  moduleSettings,
  formatMessage,
}) => {
  const menuItems = [
    {
      name: formatMessage({ id: 'JOBS', defaultMessage: 'Jobs' }),
      id: 'JOBS',
      hasPermission: true,
      Icon: IconLibrary.ServiceJobs,
      children: [
        {
          name: formatMessage({
            id: 'CREATE_JOB',
            defaultMessage: 'Create Job',
          }),
          hasPermission: permissionConfig({
            userAccess,
            permissionName: 'CREATE_JOB',
          }),
          tags: ['create', 'new', 'service', 'job', 'pre', 'register'],
          onClick: () => {
            navigate('/servicejobs/preregister');
          },
        },
        {
          name: formatMessage({
            id: 'LIST_ALL_JOBS',
            defaultMessage: 'List All Jobs',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job'],
          onClick: () => {
            store.domain.list.reset('ServiceJobList');
            navigate('/servicejobs');
          },
        },
        {
          name: formatMessage({
            id: 'SEARCH_JOBS',
            defaultMessage: 'Search Jobs',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job', 'search'],
          onClick: () => {
            navigate('/servicejobs?view=search');
          },
        },
      ],
    },
    {
      name: formatMessage({ id: 'CLAIMS', defaultMessage: 'Claims' }),
      id: 'CLAIMS',
      hasPermission: userAccess.isSASupervisor() || userAccess.isAdmin(),
      Icon: IconLibrary.Claims,
      children: [
        {
          name: formatMessage({
            id: 'CLAIM_PAYMENT_EXPORTS',
            defaultMessage: 'Claim Payment Exports',
          }),
          hasPermission: userAccess.isSASupervisor() || userAccess.isAdmin(),
          tags: ['list', 'all', 'claim', 'payment', 'export'],
          onClick: () => {
            navigate('/claim-payment-exports');
          },
        },
        {
          name: formatMessage({
            id: 'CLAIM_PAYMENT_IMPORTS',
            defaultMessage: 'Claim Payment Imports',
          }),
          hasPermission:
            userAccess.isSASupervisor() ||
            userAccess.isAdminImplementationManager(),
          tags: ['list', 'all', 'claim', 'payment', 'import'],
          onClick: () => {
            navigate('/claim-payment-import');
          },
        },
      ],
    },
    {
      name: formatMessage({
        id: 'MANAGEMENT',
        defaultMessage: 'Management',
      }),
      id: 'MANAGEMENT',
      hasPermission: true,
      Icon: IconLibrary.ServiceProviders,
      children: [
        {
          name: formatMessage({
            id: 'SERVICE_PROVIDERS',
            defaultMessage: 'Service Providers',
          }),
          hasPermission: userAccess.isSA() || userAccess.isAdmin(),
          tags: [
            'list',
            'all',
            'serviceprovider',
            'serviceproviders',
            'manage',
          ],
          onClick: () => {
            navigate('/serviceproviders');
          },
        },
        {
          name: formatMessage({
            id: 'USERS',
            defaultMessage: 'Users',
          }),
          hasPermission: true,
          tags: ['user', 'role', 'account'],
          onClick: () => {
            if (userAccess.isSA() || userAccess.isAdmin()) {
              navigate('/users');
            } else {
              const serviceProviderId = userAccess.company.id;
              navigate(`/serviceproviders/edit/${serviceProviderId}/users`);
            }
          },
        },
        {
          name: formatMessage({
            id: 'SERVICE_DIRECTORY',
            defaultMessage: 'Service Directory',
          }),
          hasPermission:
            moduleSettings?.serviceDirectoryPurchased &&
            (userAccess.isSASupervisor() || userAccess.isAdmin()),
          tags: ['Documentation'],
          onClick: () => {
            navigate('/service-directory');
          },
        },
        {
          name: formatMessage({
            id: 'INVOICES',
            defaultMessage: 'Invoices',
          }),
          hasPermission: userAccess.isSASupervisor() || userAccess.isAdmin(),
          tags: ['list', 'all', 'invoice'],
          onClick: () => {
            navigate('/invoices');
          },
        },
      ],
    },
    {
      name: formatMessage({
        id: 'SUPPORT',
        defaultMessage: 'Support',
      }),
      id: 'SUPPORT',
      hasPermission: true,
      Icon: IconLibrary.Support,
      children: [
        {
          name: formatMessage({
            id: 'API_DOCUMENTATION',
            defaultMessage: 'API Documentation',
          }),
          hasPermission: true,
          tags: ['Documentation'],
          onClick: () => {
            navigate('/docs');
          },
        },
        {
          name: formatMessage({
            id: 'HELP_TRAINING',
            defaultMessage: 'Help & Training',
          }),
          hasPermission: true,
          tags: ['Help'],
          onClick: () => {
            navigate('/help');
          },
        },
        {
          name: formatMessage({
            id: 'REQUESTS_FEEDBACK',
            defaultMessage: 'Requests & Feedback',
          }),
          hasPermission: true,
          tags: ['request', 'feedback', 'bug', 'support', 'triage'],
          onClick: () => {
            navigate('/issue-tracking');
          },
        },
        {
          name: formatMessage({
            id: 'EXPORT_HISTORY',
            defaultMessage: 'Export History',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job', 'search', 'export'],
          onClick: () => {
            navigate('/data-export-history');
          },
        },
      ],
    },
  ];

  return menuItems;
};

const setup = ({ navigate, userAccess, IconLibrary, formatMessage }) => [
  {
    name: formatMessage({ id: 'SETUP', defaultMessage: 'Setup' }),
    hasPermission: userAccess.isSA() || userAccess.isAdmin(),
    Icon: IconLibrary.Companies,
    children: [
      {
        name: formatMessage({
          id: 'INDUSTRIES',
          defaultMessage: 'Industries',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'industry', 'manage'],
        onClick: () => {
          navigate('/industries');
        },
      },
      {
        name: formatMessage({ id: 'CLIENTS', defaultMessage: 'Clients' }),
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorclient',
          'serviceadministratorclients',
          'manage',
          'client',
          'administrator',
        ],
        onClick: () => {
          navigate('/serviceadministratorclients');
        },
      },
      {
        name: formatMessage({
          id: 'PRODUCT_LINES',
          defaultMessage: 'Product Lines',
        }),
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorproductline',
          'serviceadministratorproductlines',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorproductlines');
        },
      },
      {
        name: formatMessage({
          id: 'REPAIR_SEVERITIES',
          defaultMessage: 'Repair Severities',
        }),
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorrepairseverity',
          'serviceadministratorrepairseverities',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorrepaircategories');
        },
      },
      {
        name: formatMessage({
          id: 'SERVICE_TYPES',
          defaultMessage: 'Service Types',
        }),
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorservicetype',
          'serviceadministratorservicetypes',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorservicetypes');
        },
      },
      {
        name: formatMessage({
          id: 'WARRANTY_TYPES',
          defaultMessage: 'Warranty Types',
        }),
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorwarrantytype',
          'serviceadministratorwarrantytypes',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorwarrantytypes');
        },
      },
      {
        name: formatMessage({
          id: 'SERVICE_SITES',
          defaultMessage: 'Service Sites',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'servicesite', 'servicesites', 'manage'],
        onClick: () => {
          navigate('/servicesites');
        },
      },
      {
        name: formatMessage({
          id: 'LABOR_MATRIX',
          defaultMessage: 'Labor Matrix',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'labor', 'labor matrix', 'manage'],
        onClick: () => {
          navigate('/labormatrices');
        },
      },
      {
        name: formatMessage({ id: 'BRANDS', defaultMessage: 'Brands' }),
        hasPermission: true,
        tags: ['list', 'all', 'brand', 'manage'],
        onClick: () => {
          navigate('/brands');
        },
      },
    ],
  },
  {
    name: formatMessage({
      id: 'SYSTEM_SETTINGS',
      defaultMessage: 'System Settings',
    }),
    hasPermission: userAccess.isSA() || userAccess.isAdmin(),
    Icon: IconLibrary.System,
    children: [
      {
        name: formatMessage({
          id: 'COUNTRIES',
          defaultMessage: 'Countries',
        }),
        hasPermission: userAccess.isAdmin(),
        tags: ['list', 'all', 'country', 'countries', 'manage'],
        onClick: () => {
          navigate('/countries');
        },
      },
      {
        name: formatMessage({
          id: 'JOB_FORMS',
          defaultMessage: 'Job Forms',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'jobform', 'jobforms', 'manage'],
        onClick: () => {
          navigate('/servicejobforms');
        },
      },
      {
        name: formatMessage({ id: 'RULES', defaultMessage: 'Rules' }),
        hasPermission: true,
        tags: ['list', 'all', 'rule', 'rules', 'manage'],
        onClick: () => {
          navigate('/rules');
        },
      },
      {
        name: formatMessage({
          id: 'WORKFLOWS',
          defaultMessage: 'Workflows',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'workflow', 'workflows', 'manage'],
        onClick: () => {
          navigate('/workflows');
        },
      },
      {
        name: formatMessage({
          id: 'DOCUMENT_MANAGEMENT',
          defaultMessage: 'Document Management',
        }),
        hasPermission: userAccess.isAdminImplementationManager(),
        tags: [
          'list',
          'all',
          'help',
          'training',
          'document',
          'documents',
          'manage',
        ],
        onClick: () => {
          navigate('/document-management');
        },
      },
    ],
  },
  {
    name: formatMessage({ id: 'INSIGHTS', defaultMessage: 'Insights' }),
    hasPermission: userAccess.isSA() || userAccess.isAdmin(),
    Icon: AiIconSmall,
    children: [
      {
        name: formatMessage({
          id: 'FLOW_DEFINITIONS',
          defaultMessage: 'Flow Definitions',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'flow', 'definitions', 'manage'],
        onClick: () => {
          navigate('/flow-definitions');
        },
      },
      {
        name: formatMessage({
          id: 'INSIGHT_CONFIG',
          defaultMessage: 'Insight Config',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'insight', 'config', 'manage'],
        onClick: () => {
          navigate('/insight-config');
        },
      },
      {
        name: formatMessage({
          id: 'MANAGED_NUMBERS',
          defaultMessage: 'Managed Numbers',
        }),
        hasPermission: true,
        tags: ['list', 'all', 'managed', 'number', 'phone', 'manage'],
        onClick: () => {
          navigate('/managed-numbers');
        },
      },
    ],
  },
  {
    name: formatMessage({
      id: 'REQUESTS_FEEDBACK',
      defaultMessage: 'Requests & Feedback',
    }),
    hasPermission:
      userAccess.isAdminImplementationManager() || userAccess.isSASupervisor(),
    Icon: IconLibrary.RequestsAndFeedback,
    children: [
      {
        name: formatMessage({
          id: 'CONFIGURATION',
          defaultMessage: 'Configuration',
        }),
        hasPermission: true,
        tags: ['request', 'feedback', 'notification', 'configuration'],
        onClick: () => {
          navigate('/feedback/configuration');
        },
      },
    ],
  },
];

export default { menu, setup };
